import React from 'react'
import PropTypes from 'prop-types'
import { Button, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    letterSpacing: '0.3px',
    padding: '8px 22px',
    minHeight: '40px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&.Mui-disabled': {
      transform: 'none',
      boxShadow: 'none',
    },
  },
  // Contained styles
  containedPrimary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: '0 2px 4px rgba(10, 37, 64, 0.15)',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: '0 4px 12px rgba(10, 37, 64, 0.25)',
    },
  },
  containedSecondary: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    boxShadow: '0 2px 4px rgba(14, 165, 233, 0.15)',
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
      boxShadow: '0 4px 12px rgba(14, 165, 233, 0.25)',
    },
  },
  containedSuccess: {
    backgroundColor: theme.palette.success?.main || '#15803d',
    color: theme.palette.success?.contrastText || '#ffffff',
    boxShadow: '0 2px 4px rgba(21, 128, 61, 0.15)',
    '&:hover': {
      backgroundColor: theme.palette.success?.dark || '#166534',
      boxShadow: '0 4px 12px rgba(21, 128, 61, 0.25)',
    },
  },
  containedWarning: {
    backgroundColor: theme.palette.warning?.main || '#f97316',
    color: theme.palette.warning?.contrastText || '#ffffff',
    boxShadow: '0 2px 4px rgba(244, 103, 44, 0.15)',
    '&:hover': {
      backgroundColor: theme.palette.warning?.dark || '#ea580c',
      boxShadow: '0 4px 12px rgba(244, 103, 44, 0.25)',
    },
  },
  containedError: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.15)',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
    },
  },

  // Outlined styles
  outlinedPrimary: {
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'rgba(10, 37, 64, 0.04)',
      borderColor: theme.palette.primary.dark,
    },
  },
  outlinedSecondary: {
    border: `1px solid ${theme.palette.secondary.main}`,
    color: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: 'rgba(14, 165, 233, 0.04)',
      borderColor: theme.palette.secondary.dark,
    },
  },
  outlinedSuccess: {
    border: `1px solid ${theme.palette.success?.main || '#15803d'}`,
    color: theme.palette.success?.main || '#15803d',
    '&:hover': {
      backgroundColor: 'rgba(21, 128, 61, 0.04)',
      borderColor: theme.palette.success?.dark || '#166534',
    },
  },
  outlinedWarning: {
    border: `1px solid ${theme.palette.warning?.main || '#f97316'}`,
    color: theme.palette.warning?.main || '#f97316',
    '&:hover': {
      backgroundColor: 'rgba(244, 103, 44, 0.04)',
      borderColor: theme.palette.warning?.dark || '#ea580c',
    },
  },
  outlinedError: {
    border: `1px solid ${theme.palette.error.main}`,
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: 'rgba(220, 38, 38, 0.04)',
      borderColor: theme.palette.error.dark,
    },
  },

  // Loading spinner
  spinner: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    marginLeft: -10,
    marginTop: -10,
  },
  labelHidden: {
    visibility: 'hidden',
  },
}))

export const CustomButton = (props) => {
  const {
    children,
    colorType = 'primary',
    variant = 'contained',
    isLoading = false,
    disabled = false,
    className,
    ...rest
  } = props

  const classes = useStyles()

  const getStyleClass = () => {
    if (variant === 'contained') {
      switch (colorType) {
        case 'primary': return classes.containedPrimary
        case 'secondary': return classes.containedSecondary
        case 'success': return classes.containedSuccess
        case 'warning': return classes.containedWarning
        case 'error': return classes.containedError
        default: return classes.containedPrimary
      }
    } else if (variant === 'outlined') {
      switch (colorType) {
        case 'primary': return classes.outlinedPrimary
        case 'secondary': return classes.outlinedSecondary
        case 'success': return classes.outlinedSuccess
        case 'warning': return classes.outlinedWarning
        case 'error': return classes.outlinedError
        default: return classes.outlinedPrimary
      }
    }
    return ''
  }

  return (
    <Button
      variant={variant}
      disabled={disabled || isLoading}
      className={clsx(classes.root, getStyleClass(), className)}
      {...rest}
    >
      <span className={clsx(isLoading && classes.labelHidden)} style={{ display: 'inline-flex', alignItems: 'center' }}>
        {children}
      </span>
      {isLoading && (
        <CircularProgress size={20} className={classes.spinner} color="inherit" />
      )}
    </Button>
  )
}

CustomButton.propTypes = {
  children: PropTypes.node.isRequired,
  colorType: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error']),
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
}
